import * as React from 'react'
import { cn } from '../../lib/cn'

/**
 * OtpDigitInput — N single-digit cells for one-time codes.
 *
 * Behaviour:
 *   - Numeric input only (`inputMode="numeric"`, non-digits stripped).
 *   - Typing a digit auto-advances focus to the next cell.
 *   - Backspace on an empty cell clears + focuses the previous cell.
 *   - ArrowLeft / ArrowRight navigate between cells.
 *   - Paste on any cell distributes the digits across the row from cell 1.
 *   - Cells have `aria-label="Digit N of M"` for screen readers.
 *   - `invalid` flips every cell to `aria-invalid="true"` and the error border
 *     token. Caller renders the error message + `aria-live` region itself.
 *
 * Hoisted from `apps/dp-auth-ui/src/components/mfa/OtpBoxInput.tsx` so any
 * surface that needs a numeric-OTP entry (MFA verify, email/SMS verification,
 * step-up confirmation) composes the same primitive instead of duplicating.
 *
 * No internal logging or analytics — those belong to the composing molecule
 * (e.g. VerificationOtpStep) so this primitive stays presentation-only.
 */

export interface OtpDigitInputProps {
  /** Current value as a single string of up to `length` digits. */
  value: string
  /** Called with the normalized digit string after every change. */
  onChange: (next: string) => void
  /** Called when the user presses Enter from any cell. */
  onEnter?: () => void
  /** Number of cells. Default 6. */
  length?: number
  /** Focus cell 1 on mount. Default false. */
  autoFocus?: boolean
  /** Disable every cell. */
  disabled?: boolean
  /**
   * Visual + a11y error state — adds `aria-invalid="true"` and the error
   * border token to every cell. Caller renders the message text.
   */
  invalid?: boolean
  /**
   * Optional shared accessible name used to scope the per-cell labels.
   * When set each cell becomes `Digit N of M, <name>`. Useful when the
   * cells are inside a larger labelled region (e.g. "verification code").
   */
  'aria-label'?: string
  /** Extra classes for the row wrapper. */
  className?: string
}

export const OtpDigitInput = React.forwardRef<HTMLDivElement, OtpDigitInputProps>(
  (
    {
      value,
      onChange,
      onEnter,
      length = 6,
      autoFocus = false,
      disabled = false,
      invalid = false,
      'aria-label': ariaLabel,
      className,
    },
    ref,
  ) => {
    const inputRefs = React.useRef<(HTMLInputElement | null)[]>([])
    // Normalize: pad with empty strings up to length, never longer.
    const digits = React.useMemo(() => {
      const padded = value.padEnd(length, '').split('').slice(0, length)
      return padded
    }, [value, length])

    React.useEffect(() => {
      if (autoFocus && !disabled) inputRefs.current[0]?.focus()
    }, [autoFocus, disabled])

    const writeDigits = React.useCallback(
      (next: string[]) => {
        onChange(next.join('').replace(/\s/g, ''))
      },
      [onChange],
    )

    const handleChange = React.useCallback(
      (index: number, raw: string) => {
        const cleaned = raw.replace(/\D/g, '')
        // Multi-digit landing in a single cell happens on three paths:
        //   - Browser autofill of autocomplete="one-time-code" from
        //     SMS / iMessage suggestions (whole code arrives in cell 1).
        //   - Programmatic setValue from automation (Playwright fill,
        //     password managers).
        //   - Paste pre-focused on a single input rather than the
        //     wrapper div (handlePaste won't fire).
        // Distribute starting from the current cell so any of those
        // routes lights up the whole row instead of dropping the tail.
        if (cleaned.length > 1) {
          const next = [...digits]
          for (let i = 0; i < cleaned.length && index + i < length; i++) {
            next[index + i] = cleaned[i]
          }
          writeDigits(next)
          const focusIdx = Math.min(index + cleaned.length, length - 1)
          inputRefs.current[focusIdx]?.focus()
          return
        }
        const digit = cleaned.slice(-1)
        const next = [...digits]
        next[index] = digit
        writeDigits(next)
        if (digit && index < length - 1) {
          inputRefs.current[index + 1]?.focus()
        }
      },
      [digits, length, writeDigits],
    )

    const handleKeyDown = React.useCallback(
      (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          onEnter?.()
          return
        }
        if (e.key === 'Backspace') {
          // Only hop back when the current cell is already empty.
          if (!digits[index] && index > 0) {
            e.preventDefault()
            const next = [...digits]
            next[index - 1] = ''
            writeDigits(next)
            inputRefs.current[index - 1]?.focus()
          }
          return
        }
        if (e.key === 'ArrowLeft' && index > 0) {
          e.preventDefault()
          inputRefs.current[index - 1]?.focus()
        } else if (e.key === 'ArrowRight' && index < length - 1) {
          e.preventDefault()
          inputRefs.current[index + 1]?.focus()
        }
      },
      [digits, length, onEnter, writeDigits],
    )

    const handlePaste = React.useCallback(
      (e: React.ClipboardEvent<HTMLDivElement>) => {
        const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
        if (!pasted) return
        e.preventDefault()
        onChange(pasted)
        const focusIdx = Math.min(pasted.length, length - 1)
        // Defer focus so React commits the new value first.
        window.setTimeout(() => inputRefs.current[focusIdx]?.focus(), 0)
      },
      [length, onChange],
    )

    return (
      <div
        ref={ref}
        // role=group so screen readers announce the cells as a single field
        // (each cell still has its own aria-label).
        role="group"
        aria-label={ariaLabel}
        onPaste={handlePaste}
        className={cn('flex items-center justify-center gap-2', className)}
      >
        {Array.from({ length }, (_, i) => {
          const cellLabel = ariaLabel
            ? `Digit ${i + 1} of ${length}, ${ariaLabel}`
            : `Digit ${i + 1} of ${length}`
          const filled = !!digits[i]?.trim()
          return (
            <input
              key={i}
              ref={(el) => {
                inputRefs.current[i] = el
              }}
              type="text"
              inputMode="numeric"
              // maxLength is the full row length, not 1. Reason: browser
              // autofill of autocomplete="one-time-code" pastes the WHOLE
              // code into cell 1; maxLength=1 would silently truncate it
              // to the first digit BEFORE the React onChange fires, so
              // handleChange could never see (and distribute) the rest.
              // The row-level cap still keeps the input bounded, and
              // handleChange normalises down to one digit per cell.
              maxLength={length}
              autoComplete={i === 0 ? 'one-time-code' : 'off'}
              aria-label={cellLabel}
              aria-invalid={invalid || undefined}
              value={digits[i]?.trim() || ''}
              disabled={disabled}
              onChange={(e) => handleChange(i, e.target.value)}
              onKeyDown={(e) => handleKeyDown(i, e)}
              onFocus={(e) => e.target.select()}
              // Cell width fixed so layout doesn't reflow as digits land.
              // 44px tall meets the 44x44 mobile touch-target guideline.
              className={cn(
                'w-12 h-12 rounded-md border bg-ia-input-bg text-center',
                'font-mono text-body-lg font-semibold text-ia-text tabular-nums',
                'transition-[border-color,background-color] duration-150',
                'focus-visible:outline-none focus-visible:border-ring',
                'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-ia-elevated',
                invalid
                  ? 'border-ia-border-error focus-visible:border-ia-border-error'
                  : filled
                    ? 'border-ia-border-strong'
                    : 'border-ia-border',
              )}
            />
          )
        })}
      </div>
    )
  },
)
OtpDigitInput.displayName = 'OtpDigitInput'
